/**
 * Password show/hide toggle – admin & web 공통.
 * - 단일 버튼 + 단일 아이콘(fa-eye ↔ fa-eye-slash 클래스 전환).
 * - Admin: .admin-auth__password-wrap 내 기존 버튼에 바인딩.
 * - Web: .auth-page 외부 password input을 wrap 후 토글 버튼 생성.
 * 구성 가능(options), CQS 적용, 로직은 클래스 내부로 캡슐화.
 */

export interface PasswordToggleOptions {
  readonly toggleAttr: string;
  readonly iconShow: string;
  readonly iconHide: string;
  readonly admin: {
    readonly wrapSelector: string;
    readonly toggleSelector: string;
    readonly iconSelector: string;
    readonly boundAttr: string;
  };
  readonly web: {
    readonly wrapClass: string;
    readonly wrapModifier: string;
    readonly toggleClass: string;
    readonly iconSelector: string;
    readonly excludeSelector: string;
  };
}

const DEFAULT_OPTIONS: PasswordToggleOptions = {
  toggleAttr: 'data-password-toggle-init',
  iconShow: 'fa-eye',
  iconHide: 'fa-eye-slash',
  admin: {
    wrapSelector: '.admin-auth__password-wrap',
    toggleSelector: '.admin-auth__password-toggle',
    iconSelector: '.admin-auth__toggle-icon i',
    boundAttr: 'data-password-toggle-bound',
  },
  web: {
    wrapClass: 'auth-page__input-wrap',
    wrapModifier: 'auth-page__input-wrap--password',
    toggleClass: 'auth-page__password-toggle',
    iconSelector: '.auth-page__toggle-icon i',
    excludeSelector: '.admin-auth',
  },
};

export class PasswordToggleInput {
  private readonly root: Document | Element;
  private readonly opts: PasswordToggleOptions;

  constructor(container: Document | Element = document, options: Partial<PasswordToggleOptions> = {}) {
    this.root = container;
    this.opts = { ...DEFAULT_OPTIONS, ...options };
  }

  init(): void {
    this.initAdminToggles();
    this.initWebWraps();
    this.initWebExistingWraps();
  }

  /** 뷰에 이미 렌더된 .auth-page__input-wrap--password 또는 .profile-page__input-wrap--password 내 토글 버튼 바인딩 */
  private initWebExistingWraps(): void {
    const boundAttr = this.opts.admin.boundAttr;
    const wrapSelectors = [
      `.${this.opts.web.wrapClass}.${this.opts.web.wrapModifier.replace(/\s+/g, '.')}`,
      '.profile-page__input-wrap.profile-page__input-wrap--password',
    ];
    const toggleClasses = [this.opts.web.toggleClass, 'profile-page__password-toggle'];
    wrapSelectors.forEach((wrapSelector, i) => {
      const wraps = this.root.querySelectorAll<HTMLElement>(wrapSelector);
      const toggleClass = toggleClasses[i];
      wraps.forEach((wrap) => {
        const input = this.findInputInWrap(wrap);
        const btn = wrap.querySelector<HTMLButtonElement>(`.${toggleClass}`);
        if (!input || !btn || btn.hasAttribute(boundAttr)) return;
        btn.setAttribute(boundAttr, '1');
        input.setAttribute(this.opts.toggleAttr, '1');
        const icon = this.findWebIcon(btn) ?? this.findSingleIcon(btn);
        this.setButtonAccessibility(btn, false);
        this.bindToggle(btn, input, icon);
      });
    });
  }

  // ─── Queries (CQS) ───────────────────────────────────────────────────────

  private getAriaLabel(visible: boolean): string {
    const langKo = this.isLangKo();
    if (langKo) return visible ? '비밀번호 숨기기' : '비밀번호 보기';
    return visible ? 'Hide password' : 'Show password';
  }

  private isLangKo(): boolean {
    return document.documentElement?.lang?.startsWith('ko') ?? true;
  }

  private findInputInWrap(wrap: Element): HTMLInputElement | null {
    return wrap.querySelector<HTMLInputElement>('input[type="password"], input[type="text"]');
  }

  private findWebIcon(btn: Element): Element | null {
    return btn.querySelector(this.opts.web.iconSelector);
  }

  private findSingleIcon(btn: Element): Element | null {
    return btn.querySelector(this.opts.admin.iconSelector) ?? btn.querySelector('i.fa-regular, i.fa-eye, i.fa-eye-slash');
  }

  private isPasswordVisible(input: HTMLInputElement): boolean {
    return input.type !== 'password';
  }

  // ─── Commands (CQS) ───────────────────────────────────────────────────────

  private setIconState(icon: Element, visible: boolean): void {
    icon.classList.remove(this.opts.iconShow, this.opts.iconHide);
    icon.classList.add(visible ? this.opts.iconHide : this.opts.iconShow);
  }

  private setButtonAccessibility(btn: HTMLButtonElement, visible: boolean): void {
    const label = this.getAriaLabel(visible);
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }

  private toggleInputType(input: HTMLInputElement): void {
    input.type = input.type === 'password' ? 'text' : 'password';
  }

  private bindToggle(btn: HTMLButtonElement, input: HTMLInputElement, icon: Element | null): void {
    btn.addEventListener('click', () => {
      const visible = this.isPasswordVisible(input);
      this.toggleInputType(input);
      const nowVisible = !visible;
      if (icon) this.setIconState(icon, nowVisible);
      this.setButtonAccessibility(btn, nowVisible);
    });
  }

  private initAdminToggles(): void {
    const { admin } = this.opts;
    const toggles = this.root.querySelectorAll<HTMLButtonElement>(
      `${admin.toggleSelector}:not([${admin.boundAttr}])`
    );
    toggles.forEach((btn) => {
      btn.setAttribute(admin.boundAttr, '1');
      const wrap = btn.closest(admin.wrapSelector);
      const input = wrap ? this.findInputInWrap(wrap) : null;
      if (!input) return;
      input.setAttribute(this.opts.toggleAttr, '1');
      const icon = this.findSingleIcon(btn);
      this.setButtonAccessibility(btn, false);
      this.bindToggle(btn, input, icon);
    });
  }

  private createWebToggleButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = this.opts.web.toggleClass;
    btn.setAttribute('aria-label', this.getAriaLabel(false));
    const wrapper = document.createElement('span');
    wrapper.className = 'auth-page__toggle-icon';
    const icon = document.createElement('i');
    icon.className = `fa-regular ${this.opts.iconShow}`;
    icon.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(icon);
    btn.appendChild(wrapper);
    return btn;
  }

  private initWebWraps(): void {
    const inputs = this.root.querySelectorAll<HTMLInputElement>(
      `input[type="password"]:not([${this.opts.toggleAttr}="1"])`
    );
    inputs.forEach((input) => {
      if (input.closest(this.opts.web.excludeSelector)) return;
      input.setAttribute(this.opts.toggleAttr, '1');
      const wrap = document.createElement('div');
      wrap.className = `${this.opts.web.wrapClass} ${this.opts.web.wrapModifier}`;
      const parent = input.parentElement;
      if (!parent) return;
      parent.insertBefore(wrap, input);
      wrap.appendChild(input);
      const btn = this.createWebToggleButton();
      wrap.appendChild(btn);
      const icon = this.findSingleIcon(btn);
      this.bindToggle(btn, input, icon);
    });
  }
}
